<template>
  <v-card
    class="pa-5"
    v-if="project"
    :style="{ border: '1px solid var(--primary-boarder-color)' }"
  >
    <v-row class="mb-2 pa-5 pb-1 d-flex justify-space-between align-center">
      <span class="text-h6 font-weight-bold text-black">Team Members</span>
      <v-btn
        v-if="hasPermission('project.add_member')"
        color="#00B3FF"
        depressed
        @click="openAddMemberDialog"
        class="white--text"
        >Add Member</v-btn
      >
    </v-row>

    <v-divider class="border-opacity-25"></v-divider>
    <v-list-item-group active-class="bg-white">
      <v-list-item three-line>
        <v-list-item-avatar>
          <v-avatar
            v-if="project.manager && hasPermission('project.manager_view')"
          >
            <img
              v-if="project.manager.profile_image"
              :src="project.manager.profile_image"
            />
            <img v-else :src="defaultImage" />
          </v-avatar>
        </v-list-item-avatar>

        <v-list-item-content
          v-if="project.manager && hasPermission('project.manager_view')"
        >
          <v-list-item-title
            v-html="project.manager.name"
            class="font-weight-medium text-black"
          ></v-list-item-title>
          <v-list-item-subtitle
            v-html="project.manager.employer.speciality.name"
          ></v-list-item-subtitle>
          <v-list-item-subtitle>Manager</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider></v-divider>
    </v-list-item-group>
    <v-list class="custom-list-style">
      <v-list-item-group
        v-for="(item, index) in project.members"
        :key="index"
        active-class="bg-white"
      >
        <v-list-item v-if="item.type !== 'divider'" three-line>
          <v-list-item-avatar>
            <v-avatar>
              <img v-if="item.profile_image" :src="item.profile_image" />
              <img v-else :src="defaultImage" />
            </v-avatar>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title
              v-html="item.name"
              class="font-weight-medium text-black"
            ></v-list-item-title>
            <v-list-item-subtitle
              v-html="item.employer.speciality.name"
            ></v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-icon
              v-if="hasPermission('project.delete_member')"
              @click="deleteMember(item.uuid)"
              class="delete-icon"
              color="red"
              >mdi-delete</v-icon
            >
          </v-list-item-action>
        </v-list-item>

        <v-divider v-if="index < project.members.length - 1"></v-divider>
      </v-list-item-group>
    </v-list>

    <v-dialog v-model="addMemberDialog" persistent max-width="600px">
      <v-card class="scroll-disabled pa-5">
        <v-form ref="addMemberForm" @submit.prevent="addMembersToProject">
          <v-card-title>
            <v-row align="center" justify="space-between">
              <v-col cols="auto">
                <span class="text-h6 font-weight-bold"
                  >Add Members to Project</span
                >
              </v-col>
              <v-col cols="auto">
                <v-btn
                  small
                  fab
                  depressed
                  class="mr-2 white--text"
                  color="blue"
                  sole
                  type="submit"
                >
                  <v-icon>mdi-check</v-icon>
                </v-btn>
                <v-btn
                  small
                  fab
                  depressed
                  class="mr-2 white--text"
                  color="gray"
                  sole
                  @click="close"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              v-model="selectedMembers"
              :items="filteredMembers"
              label="Select Members"
              multiple
              chips
              deletable-chips
              :item-text="formatMemberText"
              item-value="uuid"
              clearable
            ></v-autocomplete>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.text-black {
  color: #000000;
}

.custom-list-style {
  height: 300px;
  overflow-y: auto;
  position: relative;
}

.scroll-disabled {
  overflow-y: hidden;
  overflow-x: hidden;
}
</style>

<script>
import { ProjectService } from "@/services/project-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  props: {
    project: Object,
  },
  data: () => ({
    defaultImage: require("@/assets/img/avatar.svg"),
    members: [],
    addMemberDialog: false,
    selectedMembers: [],
  }),

  mounted() {
    this.getMembersList();
  },

  computed: {
    filteredMembers() {
      return this.members.filter((member) => !this.isMemberInProject(member));
    },

    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },

  methods: {
    openAddMemberDialog() {
      this.addMemberDialog = true;
    },
    formatMemberText(member) {
      if (member.employer && member.employer.speciality) {
        return `${member.employer.full_name} - ${member.employer.speciality.name}`;
      } else {
        return member.name;
      }
    },
    addMembersToProject() {
      if (this.selectedMembers.length === 0) {
        this.addMemberDialog = true;
        return;
      }

      this.$emit("members-add", this.selectedMembers);
      this.close();
    },
    getMembersList() {
      this.loading = true;
      ProjectService.getUsersList()
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.members = response.data.data;
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    },
    isMemberInProject(member) {
      return (
        this.project.members &&
        this.project.members.some(
          (projectMember) => projectMember.uuid === member.uuid
        )
      );
    },
    deleteMember(member) {
      this.$emit("members-delete", member);
    },
    close() {
      this.addMemberDialog = false;
      this.selectedMembers = [];
    },
  },
};
</script>
